import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Check, Wand2, Loader2, X } from 'lucide-react';
import VoiceMicButton from './VoiceMicButton';
import api, { getToken } from '../api';
import { API_BASE_URL } from '../constants';

/**
 * 全屏文本编辑器（带文本整理 + 按住说话）
 *
 * 给资料库那两处描述词输入框用（SaveAssetModal / DescriptionEditModal）。
 *
 * ⚠️ 为什么不直接复用 NodeConsole 里的 FullScreenPromptModal：
 *   那个组件没 export，而且耦合了 ToolbarContent（文本节点的富文本工具条：
 *   字号/字重/颜色/标题级别），资料库这边纯文本输入完全不需要。
 *   视觉规格照它抄（900px / 80vh / 圆角 20 / Check 关闭），行为保持一致。
 *   —— 它在跑着，不去动它。将来想收敛成一个再说。
 *
 * 文本整理走 SSE 流式接口 /api/ai/text-polish，整理结果**不直接替换原文**，
 * 而是弹对照面板让用户确认（与节点控制台同一套交互）。
 */

// ─────────────────────────────────────────────────────────────────
//  整理结果对照面板：左原文 / 右整理稿，右侧可直接改，确认才回写
// ─────────────────────────────────────────────────────────────────
function PolishPreview({ isOpen, originalText, polishedText, isStreaming, onConfirm, onCancel }) {
  const [edited, setEdited] = useState('');
  const userEditedRef = useRef(false);

  useEffect(() => { if (isOpen) userEditedRef.current = false; }, [isOpen]);
  // 流式推进时持续同步，但用户一旦手动改过就不再覆盖
  useEffect(() => { if (!userEditedRef.current) setEdited(polishedText || ''); }, [polishedText]);

  if (!isOpen) return null;

  return createPortal(
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 1000000, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 900, maxWidth: '95vw', height: '80vh',
          background: 'var(--modal-surface)', borderRadius: 16,
          border: '1px solid var(--border-subtle)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{
            fontSize: 15, fontWeight: 600, color: 'var(--text-body)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Wand2 size={16} color="#60a5fa" /> 描述词整理
            {isStreaming && <Loader2 size={14} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} />}
          </div>
          <div
            onClick={onCancel}
            style={{
              width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-hover)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <X size={16} color="var(--text-secondary)" />
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* 原文（只读） */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-subtle)' }}>
            <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>原文</div>
            <div className="custom-scrollbar" style={{
              flex: 1, padding: '0 16px 16px', overflowY: 'auto',
              fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap',
            }}>
              {originalText}
            </div>
          </div>
          {/* 整理稿（可改） */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '8px 16px', fontSize: 12, color: '#60a5fa', flexShrink: 0 }}>整理后（可继续编辑）</div>
            <textarea
              className="custom-scrollbar"
              value={edited}
              onChange={e => { userEditedRef.current = true; setEdited(e.target.value); }}
              style={{
                flex: 1, margin: '0 16px 16px', background: 'var(--input-bg, #0f172a)',
                border: '1px solid var(--border-strong)', borderRadius: 8, padding: 10,
                color: 'var(--text-body)', fontSize: 13, lineHeight: 1.7,
                resize: 'none', outline: 'none', fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--border-subtle)',
          display: 'flex', gap: 12, justifyContent: 'flex-end',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border-strong)',
              background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer',
            }}
          >
            放弃
          </button>
          <button
            onClick={() => onConfirm(edited)}
            disabled={isStreaming || !edited.trim()}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: (isStreaming || !edited.trim()) ? 'rgba(37,99,235,0.4)' : '#2563eb',
              color: '#fff', fontSize: 13, fontWeight: 'bold',
              cursor: (isStreaming || !edited.trim()) ? 'not-allowed' : 'pointer',
            }}
          >
            用这段
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────
//  全屏编辑器主体
// ─────────────────────────────────────────────────────────────────
export default function FullscreenTextEditor({
  isOpen,
  onClose,
  value,
  onChange,
  title = '编辑描述词',
  placeholder = '描述这张图的内容，会作为提示词传给模型…',
  // 「取消」路径：关闭且**不保存**。不传则退化成 onClose（保存并关闭）。
  // ⚠️ 必须和 onClose 分开：DescriptionEditModal 的 onClose 会调 onSave 写回描述词，
  //    少了这条路用户手滑清空内容就没法反悔。
  onDismiss,
}) {
  const textareaRef = useRef(null);
  // 本地缓冲：避免每次按键回写父级导致光标跳到末尾
  const [localText, setLocalText] = useState(value || '');
  useEffect(() => { if (isOpen) setLocalText(value || ''); }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc 关闭且不保存 —— 与右上角「取消」按钮同一条路径
  const dismiss = onDismiss || onClose;
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); dismiss(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, dismiss]);

  // 文本整理按钮显隐：取决于「文本润色设定」里是否勾了至少一个语言模型
  const [polishEnabled, setPolishEnabled] = useState(false);
  useEffect(() => {
    const check = () => {
      api.get('/api/ai/text-polish/models')
        .then(data => setPolishEnabled(Array.isArray(data?.models) && data.models.length > 0))
        .catch(() => {});
    };
    check();
    window.addEventListener('modelsUpdated', check);
    return () => window.removeEventListener('modelsUpdated', check);
  }, []);

  const [polishPreview, setPolishPreview] = useState(null); // { original, polished }
  const [isPolishing, setIsPolishing] = useState(false);
  const abortRef = useRef(null);

  const handlePolish = useCallback(async () => {
    const text = (localText || '').trim();
    if (!text) return;
    setPolishPreview({ original: text, polished: '' });
    setIsPolishing(true);
    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const response = await fetch(`${API_BASE_URL}/api/ai/text-polish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `请求失败 (${response.status})`);
      }
      // SSE 流式：逐段累加，边收边刷对照面板
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      while (true) {
        const { done, value: chunk } = await reader.read();
        if (done) break;
        buffer += decoder.decode(chunk, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (payload === '[DONE]') break;
          try {
            const json = JSON.parse(payload);
            if (json.error) throw new Error(json.error);
            if (json.content) {
              accumulated += json.content;
              // 快照一份再进闭包：直接引用循环变量 accumulated 会触发 no-loop-func
              const snapshot = accumulated;
              setPolishPreview(prev => (prev ? { ...prev, polished: snapshot } : null));
            }
          } catch (e) {
            if (e.message && e.message !== 'Unexpected end of JSON input') {
              console.error('整理流解析错误:', e);
            }
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('文本整理失败:', e);
      setPolishPreview(prev => (prev ? { ...prev, polished: `[错误] ${e.message || '整理失败'}` } : null));
    } finally {
      setIsPolishing(false);
      abortRef.current = null;
    }
  }, [localText]);

  const commit = (text) => { setLocalText(text); onChange(text); };

  if (!isOpen) return null;

  const canPolish = !isPolishing && (localText || '').trim();

  return createPortal(
    <>
      <div
        className="nodrag"
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 999999, padding: 20,
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: 900, maxWidth: '100%', height: '80vh',
            background: 'var(--modal-gradient)', borderRadius: 20,
            boxShadow: '0 25px 100px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column',
            border: '1px solid var(--border-subtle)',
          }}
        >
          {/* Header：标题居中，右侧「整理 / 说话 / 完成」*/}
          <div style={{
            height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderBottom: '1px solid var(--border-subtle)', background: 'rgba(0,0,0,0.2)',
            position: 'relative', borderRadius: '20px 20px 0 0', flexShrink: 0,
          }}>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', userSelect: 'none' }}>{title}</div>
            <div style={{
              position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {polishEnabled && (
                <div
                  onClick={() => { if (canPolish) handlePolish(); }}
                  title="文本整理"
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: isPolishing ? 'rgba(96,165,250,0.2)' : 'var(--bg-hover)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isPolishing ? 'wait' : (canPolish ? 'pointer' : 'not-allowed'),
                    transition: 'all 0.2s', opacity: canPolish ? 1 : 0.45,
                  }}
                  onMouseEnter={e => { if (!isPolishing) e.currentTarget.style.background = 'rgba(96,165,250,0.25)'; }}
                  onMouseLeave={e => { if (!isPolishing) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                >
                  {isPolishing
                    ? <Loader2 size={18} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} />
                    : <Wand2 size={18} color="#60a5fa" />}
                </div>
              )}
              <VoiceMicButton
                size={32}
                position="inline"
                currentText={localText}
                textareaRef={textareaRef}
                onResult={commit}
              />
              {/* 取消：关闭且不保存。Esc 走同一条路径。
                  ⚠️ 这个按钮不能省：只有「完成」的话，用户手滑清空内容就没法反悔。 */}
              <div
                onClick={dismiss}
                title="取消（不保存）"
                style={{
                  width: 36, height: 36, borderRadius: '50%', background: 'var(--bg-hover)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.25)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              >
                <X size={18} color="var(--text-secondary)" />
              </div>
              <div
                onClick={onClose}
                title="完成（保存）"
                style={{
                  width: 36, height: 36, borderRadius: '50%', background: 'var(--bg-hover)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--border-strong)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              >
                <Check size={20} color="var(--text-primary)" />
              </div>
            </div>
          </div>

          <textarea
            ref={textareaRef}
            data-voice-shortcut="true"
            className="custom-scrollbar"
            value={localText}
            onChange={e => commit(e.target.value)}
            placeholder={placeholder}
            autoFocus
            spellCheck={false}
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--text-primary)', fontSize: 15, lineHeight: 1.8,
              padding: '40px 60px', resize: 'none',
              fontFamily: '"PingFang SC", "Microsoft YaHei", sans-serif',
              whiteSpace: 'pre-wrap', borderRadius: '0 0 20px 20px',
            }}
          />
        </div>
      </div>

      <PolishPreview
        isOpen={!!polishPreview}
        originalText={polishPreview?.original || ''}
        polishedText={polishPreview?.polished || ''}
        isStreaming={isPolishing}
        onConfirm={(finalText) => { if (finalText) commit(finalText); setPolishPreview(null); }}
        onCancel={() => {
          if (abortRef.current) abortRef.current.abort();
          setPolishPreview(null);
          setIsPolishing(false);
        }}
      />
    </>,
    document.body
  );
}
